import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormHelperText,
} from "@mui/material";

const ClassSectionForm = ({
  initialData,
  onSubmit,
  onCancel,
  buttonLoading,
  academicYears = {},
  coordinators = [],
  participants = [],
  departments = [],
}) => {
  const [form, setForm] = useState({
    title: "",
    department: "",
    academicYear: "",
    sectionCoordinator: "",
    programCoordinators: [],
    courseParticipants: [],
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setForm({
        title: initialData.title || "",
        department: initialData.department || "",
        academicYear: initialData.academicYear || "",
        sectionCoordinator: initialData.sectionCoordinator || "",
        programCoordinators: initialData.programCoordinators || [],
        courseParticipants: initialData.courseParticipants || [],
        classSectionId: initialData.classSectionId || "",
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = () => {
    let temp = {};
    if (!form.title) temp.title = "Section title is required";
    if (!form.department) temp.department = "Department is required";
    if (!form.academicYear) temp.academicYear = "Academic year is required";
    if (!form.programCoordinators.length)
      temp.programCoordinators = "Program coordinators are required";
    if (!form.courseParticipants.length)
      temp.courseParticipants = "Course participants are required";
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit(form);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography
        variant="h6"
        fontWeight={600}
        color="text.secondary"
        mb={2}
        textAlign="center"
      >
        {initialData ? "Edit Class Section" : "Create Class Section"}
      </Typography>

      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
        <TextField
          label="Section Title *"
          name="title"
          value={form.title}
          onChange={handleChange}
          size="small"
          fullWidth
          error={!!errors.title}
          helperText={errors.title}
        />

        <FormControl fullWidth size="small" error={!!errors.department}>
          <InputLabel>Department *</InputLabel>
          <Select
            name="department"
            value={form.department}
            onChange={handleChange}
            label="Department *"
            sx={{ textTransform: "capitalize" }}
            input={<OutlinedInput label="Department *" />}
          >
            {departments.length === 0 ? (
              <MenuItem key="no-data" disabled>
                No data available
              </MenuItem>
            ) : (
              departments.map((dept) => (
                <MenuItem
                  key={dept.title}
                  value={dept.title}
                  sx={{ textTransform: "capitalize" }}
                >
                  {dept.title}
                </MenuItem>
              ))
            )}
          </Select>
          <FormHelperText>{errors.department}</FormHelperText>
        </FormControl>

        <FormControl fullWidth size="small" error={!!errors.academicYear}>
          <InputLabel>Academic Year *</InputLabel>
          <Select
            name="academicYear"
            value={form.academicYear}
            onChange={handleChange}
            label="Academic Year *"
            sx={{ textTransform: "capitalize" }}
            input={<OutlinedInput label="Academic Year *" />}
          >
            {!academicYears?.academicYearId ? (
              <MenuItem key="no-data" disabled>
                No data available
              </MenuItem>
            ) : (
              <MenuItem
                key={academicYears.academicYearId}
                value={academicYears.academicYearId}
                sx={{ textTransform: "capitalize" }}
              >
                {academicYears.name}
              </MenuItem>
            )}
          </Select>
          <FormHelperText>{errors.academicYear}</FormHelperText>
        </FormControl>

        <FormControl fullWidth size="small">
          <InputLabel>Section Coordinator</InputLabel>
          <Select
            name="sectionCoordinator"
            value={form.sectionCoordinator}
            onChange={handleChange}
            label="Section Coordinator"
            sx={{ textTransform: "capitalize" }}
            input={<OutlinedInput label="Section Coordinator" />}
          >
            {coordinators.length === 0 ? (
              <MenuItem key="no-data" disabled>
                No data available
              </MenuItem>
            ) : (
              [
                <MenuItem key="reset" value="">
                  <em>Select Section Coordinator</em>
                </MenuItem>,
                ...coordinators.map((c) => (
                  <MenuItem
                    key={c.userId}
                    value={c.userId}
                    sx={{ textTransform: "capitalize" }}
                  >
                    {c.name}
                  </MenuItem>
                )),
              ]
            )}
          </Select>
        </FormControl>

        <FormControl
          fullWidth
          size="small"
          error={!!errors.programCoordinators}
        >
          <InputLabel>Program Coordinators *</InputLabel>
          <Select
            multiple
            name="programCoordinators"
            value={form.programCoordinators}
            onChange={handleChange}
            sx={{ textTransform: "capitalize" }}
            input={<OutlinedInput label="Program Coordinators *" />}
            renderValue={(selected) =>
              coordinators
                .filter((c) => selected.includes(c.userId))
                .map((c) => c.name)
                .join(", ")
            }
          >
            {coordinators.length === 0 ? (
              <MenuItem key="no-data" disabled>
                No data available
              </MenuItem>
            ) : (
              coordinators.map((c) => (
                <MenuItem
                  key={c.userId}
                  value={c.userId}
                  sx={{ textTransform: "capitalize" }}
                >
                  <Checkbox
                    checked={form.programCoordinators.includes(c.userId)}
                  />
                  <ListItemText primary={c.name} />
                </MenuItem>
              ))
            )}
          </Select>
          <FormHelperText>{errors.programCoordinators}</FormHelperText>
        </FormControl>

        <FormControl fullWidth size="small" error={!!errors.courseParticipants}>
          <InputLabel>Course Participants *</InputLabel>
          <Select
            multiple
            name="courseParticipants"
            value={form.courseParticipants}
            onChange={handleChange}
            sx={{ textTransform: "capitalize" }}
            input={<OutlinedInput label="Course Participants *" />}
            renderValue={(selected) =>
              participants
                .filter((p) => selected.includes(p.userId))
                .map((p) => p.name)
                .join(", ")
            }
          >
            {participants.length === 0 ? (
              <MenuItem key="no-data" disabled>
                No data available
              </MenuItem>
            ) : (
              participants.map((p) => (
                <MenuItem
                  key={p.userId}
                  value={p.userId}
                  sx={{ textTransform: "capitalize" }}
                >
                  <Checkbox
                    checked={form.courseParticipants.includes(p.userId)}
                  />
                  <ListItemText primary={p.name} />
                </MenuItem>
              ))
            )}
          </Select>
          <FormHelperText>{errors.courseParticipants}</FormHelperText>
        </FormControl>
      </Box>

      <Box mt={3} display="flex" justifyContent="flex-end" gap={1.5}>
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{ textTransform: "none", borderRadius: "8px" }}
        >
          Cancel
        </Button>

        <Button
          type="submit"
          variant="contained"
          loading={buttonLoading}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            backgroundColor: "rgb(35, 71, 159)",
          }}
        >
          {initialData ? "Update" : "Create"}
        </Button>
      </Box>
    </Box>
  );
};

export default ClassSectionForm;
